/**
 * Renderer registry for chat content items.
 *
 * The wire shape of a message's `content[]` is `ChatContentItem[]` (from
 * `@wg/types/chat-content`). MessageBubble dispatches each item to a
 * renderer in this map keyed on `item.type`. Unknown types fall through
 * to `UnknownRenderer` so a backend that ships a new content kind ahead
 * of this client degrades gracefully rather than crashing.
 *
 * Adding a new kind:
 *   1. Add the typed shape in `@wg/types/chat-content`.
 *   2. Build a renderer in `./renderers/<Kind>Renderer.tsx`.
 *   3. Register it here.
 *   4. Optionally update the SSE backend tool that emits it.
 *
 * Renderers receive `RenderCtx` for theme tokens, the send-message callback
 * (used by interactive components to ship structured replies), locale, and
 * an `isHistory` flag (true when re-rendering past messages — interactive
 * items render inert in that state).
 */
import React, { Suspense, lazy } from 'react';
import type { ChatContentItem } from '@wg/types';
import type { ActionDeps } from './actions';

import TextRenderer from './renderers/TextRenderer';
import LoadingRenderer from './renderers/LoadingRenderer';
import ImageRenderer from './renderers/ImageRenderer';
import CardRenderer from './renderers/CardRenderer';
import QuickRepliesRenderer from './renderers/QuickRepliesRenderer';
import MultiSelectRenderer from './renderers/MultiSelectRenderer';
import ConfirmRenderer from './renderers/ConfirmRenderer';
import FormRenderer from './renderers/FormRenderer';
import AvailabilityRenderer from './renderers/AvailabilityRenderer';
import HtmlRenderer from './renderers/HtmlRenderer';
import CarouselRenderer from './renderers/CarouselRenderer';
import AppActionRenderer from './renderers/AppActionRenderer';
import PageActionRenderer from './renderers/PageActionRenderer';
import MapRenderer from './renderers/MapRenderer';
import UnknownRenderer from './renderers/UnknownRenderer';

// Lazy-loaded — Markdown pulls in react-markdown + remark-gfm (~50KB gz).
// Most short messages don't need it; we only pay the parse cost when an
// `assistant` text bubble is non-trivial or a `markdown` content item lands.
const MarkdownRenderer = lazy(() => import('./renderers/MarkdownRenderer'));

export interface PartnerTheme {
  /** Primary action / chip / link colour. */
  primary: string;
  primaryText: string;
  surface: string;
  surfaceMuted: string;
  text: string;
  textMuted: string;
  accent: string;
  destructive: string;
  /** Border radius in px. */
  radius: number;
  font: string;
}

export const DEFAULT_THEME: PartnerTheme = {
  primary: '#2658e0',
  primaryText: '#ffffff',
  surface: '#ffffff',
  surfaceMuted: '#f1f3f9',
  text: '#0e1330',
  textMuted: '#5a6184',
  accent: '#f3a712',
  destructive: '#d94c4c',
  radius: 12,
  font: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
};

export interface ResponseMeta {
  in_response_to: string;
  structured_value: unknown;
  /** Optional richer envelope. */
  [k: string]: unknown;
}

export interface RenderCtx {
  sessionId: string;
  /**
   * Send a chat message on behalf of an interactive component (chip tap,
   * form submit, confirm). The shared chat client will attach `meta` as
   * `metadata` on the request body so the agent can use the structured
   * choice rather than fuzzy-parsing the user's text.
   */
  onSendMessage: (text: string, meta?: ResponseMeta) => void;
  theme: PartnerTheme;
  locale: string;
  /**
   * True when this content item is being re-rendered as history (the user
   * already moved past this turn). Interactive renderers should display
   * the chosen answer as inert (e.g., "✓ 2 selected") rather than live
   * tappable chips.
   */
  isHistory: boolean;
  /**
   * Stable id for the user's previously-recorded answer to this prompt,
   * if any. Set by the assembler when the next user message has matching
   * `in_response_to`. Renderers use it to highlight the chosen option.
   */
  answeredWith?: unknown;
  /**
   * Bag of dependencies for the action dispatcher (see chat/actions.ts).
   * CardRenderer / AppActionRenderer / PageActionRenderer use this to
   * fire side effects (navigate, prefill_checkout, highlight, …).
   * Optional so legacy call-sites that don't need it can pass partial ctx.
   */
  actionDeps?: ActionDeps;
  /**
   * Set of `prompt_id`s for `quick_replies` items that ChatView has hoisted
   * into the docked chip strip above the input. The QuickRepliesRenderer
   * checks this to suppress the inline render so chips don't appear twice
   * (once in the bubble, once in the dock). History bubbles ignore this —
   * past chips always render inline so the user can see what they chose.
   */
  dockedPromptIds?: ReadonlySet<string>;
}

export type Renderer = (props: {
  item: ChatContentItem;
  ctx: RenderCtx;
}) => React.ReactNode;

const REGISTRY: Record<string, Renderer> = {
  text: TextRenderer,
  loading: LoadingRenderer,
  markdown: ({ item, ctx }) => (
    <Suspense fallback={<TextRenderer item={item} ctx={ctx} />}>
      <MarkdownRenderer item={item} ctx={ctx} />
    </Suspense>
  ),
  image: ImageRenderer,
  card: CardRenderer,
  quick_replies: QuickRepliesRenderer,
  multi_select: MultiSelectRenderer,
  confirm: ConfirmRenderer,
  form: FormRenderer,
  availability: AvailabilityRenderer,
  html: HtmlRenderer,
  carousel: CarouselRenderer,
  app_action: AppActionRenderer,
  page_action: PageActionRenderer,
  map: MapRenderer,
};

/**
 * Apply the partner theme to the chat root element via CSS custom
 * properties. Renderers consume these via `var(--wg-primary)` etc.,
 * which avoids prop-drilling theme to every leaf component.
 *
 * Returns inline style; assign to the chat container's root div.
 */
export function themeToCssVars(theme: PartnerTheme): React.CSSProperties {
  return {
    ['--wg-primary' as never]: theme.primary,
    ['--wg-primary-text' as never]: theme.primaryText,
    ['--wg-surface' as never]: theme.surface,
    ['--wg-surface-muted' as never]: theme.surfaceMuted,
    ['--wg-text' as never]: theme.text,
    ['--wg-text-muted' as never]: theme.textMuted,
    ['--wg-accent' as never]: theme.accent,
    ['--wg-destructive' as never]: theme.destructive,
    ['--wg-radius' as never]: `${theme.radius}px`,
    ['--wg-font' as never]: theme.font,
  };
}

export function renderItem(
  item: ChatContentItem,
  ctx: RenderCtx,
  index: number,
): React.ReactNode {
  const R = REGISTRY[item.type] ?? UnknownRenderer;
  return <R key={index} item={item} ctx={ctx} />;
}
